/**
 * client/assetload/sharedTextures.ts — GLTFLoader plugin: one THREE.Texture per image URL across GLB files.
 *
 * OWNER: ASSETS agent (registered by render/modelcache.ts and viewmodel/viewmodel.ts, CONTRACT-REQUESTS #A1).
 *
 * tools/web/optimize_textures.py moves the images out of models/*.glb into content-addressed files under
 * models/tex/ (the SEAL sleeve arms, datapad and knife maps were embedded in every viewmodel GLB: 28 MB of
 * 50 MB embedded image bytes were duplicates). The browser HTTP cache then downloads each file once, but
 * GLTFLoader still makes a new Texture per GLB (its texture cache is per parse), so each copy would be
 * uploaded to the GPU again (ESTIMATE ~10.7 MB VRAM per 2048x1024 RGBA arm normal map with mips).
 *
 * This plugin wraps the parser's loadTextureImage (the funnel used by the default path AND the
 * EXT_texture_webp / KHR_texture_basisu plugins) and returns the same Texture promise for the same resolved
 * image URL + sampler. Embedded / data: images are left to GLTFLoader.
 *
 * Use one cache per material pipeline: consumers that mutate textures differently (render/modelmat.ts sets
 * colorSpace = NoColorSpace; the viewmodel's built-in materials keep GLTFLoader's sRGB) must not share.
 */
import { LoaderUtils, type Texture } from 'three';
import type { GLTFLoaderPlugin, GLTFParser } from 'three/examples/jsm/loaders/GLTFLoader.js';

export type SharedTextureCache = Map<string, Promise<Texture | null>>;

interface ParserInternals {
  json: {
    images?: { uri?: string }[];
    textures?: { sampler?: number }[];
    samplers?: Record<string, unknown>[];
  };
  options: { path?: string };
  loadTextureImage(textureIndex: number, sourceIndex: number, loader: unknown): Promise<Texture | null>;
}

/** Cache key for one texture of one parse, or null when the image is embedded (not shareable). */
export function sharedTextureKey(p: ParserInternals, textureIndex: number, sourceIndex: number): string | null {
  const uri = p.json.images?.[sourceIndex]?.uri;
  if (typeof uri !== 'string' || uri.startsWith('data:') || uri.startsWith('blob:')) return null;
  const samplerIdx = p.json.textures?.[textureIndex]?.sampler;
  const sampler = samplerIdx === undefined ? {} : p.json.samplers?.[samplerIdx] ?? {};
  return `${LoaderUtils.resolveURL(uri, p.options.path ?? '')}|${JSON.stringify(sampler)}`;
}

/** `loader.register(sharedTexturesPlugin(cache))` */
export function sharedTexturesPlugin(cache: SharedTextureCache = new Map()): (parser: GLTFParser) => GLTFLoaderPlugin {
  return (parser: GLTFParser) => {
    const p = parser as unknown as ParserInternals;
    const own = p.loadTextureImage.bind(p);
    p.loadTextureImage = (textureIndex, sourceIndex, loader) => {
      const key = sharedTextureKey(p, textureIndex, sourceIndex);
      if (key === null) return own(textureIndex, sourceIndex, loader);
      let hit = cache.get(key);
      if (!hit) {
        hit = own(textureIndex, sourceIndex, loader);
        cache.set(key, hit);
        // a failed load (GLTFLoader resolves null) must not poison later loads of the same URL
        void hit.then((t) => { if (t === null && cache.get(key) === hit) cache.delete(key); });
      }
      return hit;
    };
    return { name: 'BO2SNS_shared_textures' };
  };
}
